import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { useLocation } from "react-router-dom";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sparkles, X, Award, Sticker, Download, Loader2, ToggleLeft, ToggleRight } from "lucide-react";
import { DIPLOMAS, DiplomaCard } from "./DiplomaCard";

const STICKERS_GENERAL = [
  // Pediatra (Oci)
  { id: "wave", label: "Saludando" },
  { id: "happy", label: "Feliz" },
  { id: "think", label: "Pensando" },
  { id: "listen", label: "Escuchando" },
  { id: "roar", label: "Rugiendo" },
  { id: "yawn", label: "Bostezando" },
  { id: "blink", label: "Guiñando" },
  { id: "ped_celebra", label: "Celebrando" },
  { id: "ped_corre", label: "Corriendo" },
  { id: "ped_salta", label: "Saltando" },
  { id: "ped_aplaude", label: "Aplaudiendo" },
  { id: "ped_lee", label: "Leyendo" },
  { id: "ped_corazon", label: "Con cariño" },
  { id: "ped_pulgar", label: "¡Muy bien!" },
  { id: "ped_abrazo", label: "Abrazo" },
  { id: "surgeon", label: "Cirujano" },
  { id: "explorer", label: "Explorador" },
  { id: "astronaut", label: "Astronauta" },
  { id: "athlete", label: "Deportista" },
  // Adulto (Oci Adulto)
  { id: "adult_wave", label: "Adulto saluda" },
  { id: "adult_happy", label: "Adulto feliz" },
  { id: "adult_think", label: "Adulto piensa" },
  { id: "adult_explica", label: "Adulto explica" },
  { id: "adult_seguro", label: "Adulto seguro" },
  { id: "adult_empatico", label: "Adulto empático" },
  { id: "adult_motivado", label: "Adulto motiva" },
  // Mayor (Oci Mayor)
  { id: "elder_wave", label: "Mayor saluda" },
  { id: "elder_happy", label: "Mayor feliz" },
  { id: "elder_think", label: "Mayor piensa" },
  { id: "elder_sabio", label: "Mayor sabio" },
  { id: "elder_explica", label: "Mayor explica" },
  { id: "elder_alegre", label: "Mayor alegre" },
  { id: "elder_aprueba", label: "Mayor aprueba" },
  { id: "elder_empatico", label: "Mayor empático" },
  { id: "elder_tranquilo", label: "Mayor tranquilo" },
];
const STICKERS_DENTAL = [
  { id: "dental_wave", label: "Saludando" },
  { id: "dental_happy", label: "Feliz" },
  { id: "dental_think", label: "Pensando" },
];

export default function FloatingClinFun() {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("diplomas");
  const [type, setType] = useState("valiente");
  const [patientName, setPatientName] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [togglingDent, setTogglingDent] = useState(false);
  const cardRef = useRef(null);
  const dragging = useRef(false);
  const posKey = "float_pos_clinfun";
  const saved = (() => { try { return JSON.parse(localStorage.getItem(posKey) || "null"); } catch { return null; } })();
  const x = useMotionValue(saved?.x || 0);
  const y = useMotionValue(saved?.y || 0);

  useEffect(() => {
    const h = (e) => { if (e.detail === "clinfun") setOpen(true); };
    window.addEventListener("clingo-open", h);
    return () => window.removeEventListener("clingo-open", h);
  }, []);

  const hasDent = !!user?.has_odontogram;
  // Exclusividad por suscripción: ClinDent solo ve lo de ClinDent; ClinPed solo lo de ClinPed.
  // "valiente" (scope both) es el único compartido en diplomas.
  const diplomas = DIPLOMAS.filter((d) => d.scope === "both" || d.scope === (hasDent ? "clindent" : "clinped"));
  const stickers = hasDent ? STICKERS_DENTAL : STICKERS_GENERAL;

  useEffect(() => {
    if (!open) return;
    setQrUrl("");
    const m = location.pathname.match(/\/pacientes\/([^/]+)/);
    if (m) api.get(`/patients/${m[1]}`).then((r) => setPatientName(r.data?.name || "")).catch(() => {});
  }, [open, location.pathname]);

  useEffect(() => { if (!diplomas.some((d) => d.key === type)) setType(diplomas[0]?.key || "valiente"); }, [hasDent]); // eslint-disable-line

  const capture = async () => {
    try { if (document.fonts?.ready) await document.fonts.ready; } catch {}
    const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `diploma-${(patientName || "oci").replace(/\s+/g, "_")}.png`;
    a.click();
  };

  const generate = async () => {
    if (!patientName.trim()) { toast.error("Escribe el nombre del paciente"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/recognitions", { patient_name: patientName, type });
      setQrUrl(`${window.location.origin}/reconocimiento/${data.id}`);
      await new Promise((r) => setTimeout(r, 400));
      await capture();
      toast.success("Diploma descargado. ¡Compártelo por WhatsApp!");
    } catch { toast.error("No se pudo generar el diploma"); }
    finally { setBusy(false); }
  };

  const toggleDent = async () => {
    setTogglingDent(true);
    try {
      const { data } = await api.post("/admin/self-clindent", { enabled: !hasDent });
      setUser(data);
      toast.success(!hasDent ? "ClinDent activado (revisión)" : "ClinDent desactivado");
    } catch { toast.error("No se pudo cambiar ClinDent"); }
    finally { setTogglingDent(false); }
  };

  const dlSticker = (name) => {
    const a = document.createElement("a");
    a.href = `/oci/${name}.png`;
    a.download = `oci-${name}.png`;
    a.click();
  };

  return (
    <>
      <motion.button
        drag dragMomentum={false} dragElastic={0} style={{ x, y }}
        onDragStart={() => { dragging.current = true; }}
        onDragEnd={() => { localStorage.setItem(posKey, JSON.stringify({ x: x.get(), y: y.get() })); setTimeout(() => (dragging.current = false), 40); }}
        onClick={() => { if (dragging.current) return; setOpen(true); }}
        data-testid="float-clinfun-toggle"
        title="ClinFun · Reconocimientos y etiquetas · arrástrame"
        whileTap={{ scale: 0.92 }} whileHover={{ scale: 1.06 }}
        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="fixed bottom-[20rem] right-6 z-[60] w-14 h-14 hidden md:flex flex-col items-center justify-center rounded-full text-white shadow-xl cursor-grab active:cursor-grabbing bg-[#e05a9c] shadow-[#e05a9c]/30"
      >
        <Sparkles size={20} />
        <span className="text-[8px] font-extrabold leading-none mt-0.5 tracking-wide">ClinFun</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/50 flex md:items-center md:justify-center"
            onClick={() => setOpen(false)}>
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="float-clinfun-window"
              className="bg-white flex flex-col overflow-hidden shadow-2xl fixed inset-0 md:inset-auto md:w-[560px] md:max-w-[94vw] md:h-[88vh] md:max-h-[880px] md:rounded-2xl">
              <div className="flex items-center justify-between px-4 h-12 bg-[#e05a9c] text-white shrink-0">
                <span className="flex items-center gap-2 font-semibold text-sm"><Sparkles size={16} /> ClinFun</span>
                <button onClick={() => setOpen(false)} data-testid="float-clinfun-close" className="p-1.5 rounded-lg hover:bg-white/10"><X size={18} /></button>
              </div>

              <div className="flex border-b border-stone-100 shrink-0">
                <button data-testid="clinfun-tab-diplomas" onClick={() => setTab("diplomas")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-bold transition-colors ${tab === "diplomas" ? "text-[#e05a9c] border-b-2 border-[#e05a9c]" : "text-stone-500"}`}>
                  <Award size={16} /> Reconocimientos
                </button>
                <button data-testid="clinfun-tab-stickers" onClick={() => setTab("stickers")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-bold transition-colors ${tab === "stickers" ? "text-[#e05a9c] border-b-2 border-[#e05a9c]" : "text-stone-500"}`}>
                  <Sticker size={16} /> Etiquetas
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {user?.role === "admin" && (
                  <button data-testid="clinfun-admin-clindent" onClick={toggleDent} disabled={togglingDent}
                    className="mb-4 w-full flex items-center justify-between gap-2 bg-stone-50 hover:bg-stone-100 rounded-xl px-3 py-2 text-sm">
                    <span className="text-stone-600 font-semibold">Admin: revisar como ClinDent</span>
                    {togglingDent ? <Loader2 size={18} className="animate-spin text-[#0D47A1]" /> : (hasDent ? <ToggleRight size={22} className="text-[#0D47A1]" /> : <ToggleLeft size={22} className="text-stone-400" />)}
                  </button>
                )}

                {tab === "diplomas" ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wide text-stone-500">Nombre del paciente</label>
                      <input data-testid="clinfun-name-input" value={patientName} onChange={(e) => setPatientName(e.target.value)}
                        placeholder="Ej. Sofía Martínez" className="w-full mt-1 rounded-xl border border-stone-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#e05a9c]/40" />
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="clinfun-diploma-types">
                      {diplomas.map((d) => (
                        <button key={d.key} data-testid={`diploma-type-${d.key}`} onClick={() => { setType(d.key); setQrUrl(""); }}
                          style={type === d.key ? { background: d.accent, color: "#fff", borderColor: d.accent } : {}}
                          className="px-3 py-2 rounded-xl text-xs font-bold border border-stone-200 text-stone-600 transition-all">
                          {d.title} {d.team === "ClinDent" ? "🦷" : ""}
                        </button>
                      ))}
                    </div>
                    <div className="overflow-auto rounded-xl border border-stone-100 bg-stone-50 p-3 flex justify-center">
                      <div style={{ transform: "scale(0.62)", transformOrigin: "top center", marginBottom: -180 }}>
                        <DiplomaCard ref={cardRef} type={type} patientName={patientName} qrUrl={qrUrl} signatureUrl={user?.signature} />
                      </div>
                    </div>
                    <button data-testid="clinfun-generate-btn" onClick={generate} disabled={busy}
                      className="w-full flex items-center justify-center gap-2 bg-[#e05a9c] hover:opacity-90 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60">
                      {busy ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                      Generar y descargar (PNG)
                    </button>
                    <p className="text-[11px] text-stone-400 text-center">Descárgalo y envíalo por WhatsApp. El QR permite a los papás descargar su diploma.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[11px] text-stone-500 bg-stone-50 rounded-lg px-3 py-2 leading-snug">
                      Toca una etiqueta de Oci para descargarla y compartirla con tus pacientes por WhatsApp.
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="clinfun-stickers">
                      {stickers.map((s) => (
                        <button key={s.id} data-testid={`sticker-${s.id}`} onClick={() => dlSticker(s.id)} title={s.label}
                          className="group relative rounded-2xl bg-stone-50 hover:bg-stone-100 p-2 flex flex-col items-center gap-1 transition-all">
                          <div className="relative aspect-square w-full flex items-center justify-center">
                            <img src={`/oci/${s.id}.png`} alt={s.label} className="max-w-full max-h-full object-contain" />
                            <span className="absolute bottom-0 right-0 bg-[#e05a9c] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Download size={12} /></span>
                          </div>
                          <span className="text-[10px] font-semibold text-stone-600 leading-none text-center truncate w-full">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
